import { baseApi, type ApiResponse } from './client';

/**
 * Admin Projects Types
 */
export interface Project {
  id: string;
  code: string;
  name: string;
  description?: string;
  status: 'active' | 'inactive' | 'archived';
  settings?: ProjectSettings;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  tags?: string[];
  metadata?: Record<string, any>;
}

export interface ProjectSettings {
  apiEnabled?: boolean;
  webhookUrl?: string;
  maxUsers?: number;
  features?: string[];
  customConfig?: Record<string, any>;
}

export interface CreateProjectRequest {
  code: string;
  name: string;
  description?: string;
  status?: 'active' | 'inactive';
  settings?: ProjectSettings;
  tags?: string[];
}

export interface UpdateProjectRequest {
  name?: string;
  description?: string;
  status?: 'active' | 'inactive' | 'archived';
  tags?: string[];
}

export interface UpdateProjectSettingsRequest {
  settings: ProjectSettings;
}

export interface ProjectFilters {
  status?: 'active' | 'inactive' | 'archived';
  search?: string;
  tags?: string[];
  createdBy?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface ProjectListParams extends ProjectFilters {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

/**
 * Admin Projects API
 * RTK Query endpoints for project management
 */
export const adminProjectsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Get all projects with pagination and filters
     */
    getProjects: builder.query<ApiResponse<Project[]>, ProjectListParams | void>({
      query: (params) => ({
        url: '/admin/projects',
        params: params || {},
      }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'Project' as const, id })),
              { type: 'Project', id: 'LIST' },
            ]
          : [{ type: 'Project', id: 'LIST' }],
    }),

    /**
     * Get project by ID
     */
    getProjectById: builder.query<ApiResponse<Project>, string>({
      query: (id) => `/admin/projects/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Project', id }],
    }),

    /**
     * Get project by code
     */
    getProjectByCode: builder.query<ApiResponse<Project>, string>({
      query: (code) => `/admin/projects/code/${code}`,
      providesTags: (_result, _error, code) => [{ type: 'Project', id: code }],
    }),

    /**
     * Create new project
     */
    createProject: builder.mutation<ApiResponse<Project>, CreateProjectRequest>({
      query: (project) => ({
        url: '/admin/projects',
        method: 'POST',
        body: project,
      }),
      invalidatesTags: [{ type: 'Project', id: 'LIST' }],
    }),

    /**
     * Update project
     */
    updateProject: builder.mutation<
      ApiResponse<Project>,
      { id: string; data: UpdateProjectRequest }
    >({
      query: ({ id, data }) => ({
        url: `/admin/projects/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Project', id },
        { type: 'Project', id: 'LIST' },
      ],
    }),

    /**
     * Delete project (soft delete)
     */
    deleteProject: builder.mutation<ApiResponse<void>, string>({
      query: (id) => ({
        url: `/admin/projects/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Project', id },
        { type: 'Project', id: 'LIST' },
      ],
    }),

    /**
     * Get project settings
     */
    getProjectSettings: builder.query<ApiResponse<ProjectSettings>, string>({
      query: (id) => `/admin/projects/${id}/settings`,
      providesTags: (_result, _error, id) => [{ type: 'Project', id: `${id}-settings` }],
    }),

    /**
     * Update project settings
     */
    updateProjectSettings: builder.mutation<
      ApiResponse<ProjectSettings>,
      { id: string; data: UpdateProjectSettingsRequest }
    >({
      query: ({ id, data }) => ({
        url: `/admin/projects/${id}/settings`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Project', id },
        { type: 'Project', id: `${id}-settings` },
      ],
    }),

    /**
     * Reset project settings to defaults
     */
    resetProjectSettings: builder.mutation<ApiResponse<ProjectSettings>, string>({
      query: (id) => ({
        url: `/admin/projects/${id}/settings/reset`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Project', id },
        { type: 'Project', id: `${id}-settings` },
      ],
    }),

    /**
     * Get default project settings
     */
    getDefaultSettings: builder.query<ApiResponse<ProjectSettings>, void>({
      query: () => '/admin/projects/settings/defaults',
    }),

    /**
     * Toggle project active status
     */
    toggleProjectStatus: builder.mutation<ApiResponse<Project>, string>({
      query: (id) => ({
        url: `/admin/projects/${id}/toggle`,
        method: 'PATCH',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Project', id },
        { type: 'Project', id: 'LIST' },
      ],
    }),

    /**
     * Restore archived project
     */
    restoreProject: builder.mutation<ApiResponse<Project>, string>({
      query: (id) => ({
        url: `/admin/projects/${id}/restore`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Project', id },
        { type: 'Project', id: 'LIST' },
      ],
    }),

    /**
     * Get project filters/statistics
     */
    getProjectFilters: builder.query<
      ApiResponse<{ statuses: string[]; tags: string[]; creators: string[] }>,
      void
    >({
      query: () => '/admin/projects/filters',
    }),

    /**
     * Search projects
     */
    searchProjects: builder.query<ApiResponse<Project[]>, { query: string; limit?: number }>({
      query: (params) => ({
        url: '/admin/projects/search',
        params,
      }),
    }),

    /**
     * Duplicate project
     */
    duplicateProject: builder.mutation<
      ApiResponse<Project>,
      { id: string; name?: string; code?: string }
    >({
      query: ({ id, ...data }) => ({
        url: `/admin/projects/${id}/duplicate`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'Project', id: 'LIST' }],
    }),

    /**
     * Export project data
     */
    exportProject: builder.query<Blob, { id: string; format?: 'json' | 'csv' }>({
      query: ({ id, format = 'json' }) => ({
        url: `/admin/projects/${id}/export`,
        params: { format },
        responseHandler: (response) => response.blob(),
      }),
    }),

    /**
     * Get project statistics
     */
    getProjectStats: builder.query<
      ApiResponse<{
        total: number;
        active: number;
        inactive: number;
        archived: number;
        recentlyCreated: number;
      }>,
      void
    >({
      query: () => '/admin/projects/stats',
      providesTags: [{ type: 'Project', id: 'STATS' }],
    }),
  }),
});

/**
 * Export hooks for components
 */
export const {
  useGetProjectsQuery,
  useGetProjectByIdQuery,
  useGetProjectByCodeQuery,
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useDeleteProjectMutation,
  useGetProjectSettingsQuery,
  useUpdateProjectSettingsMutation,
  useResetProjectSettingsMutation,
  useGetDefaultSettingsQuery,
  useToggleProjectStatusMutation,
  useRestoreProjectMutation,
  useGetProjectFiltersQuery,
  useSearchProjectsQuery,
  useDuplicateProjectMutation,
  useLazyExportProjectQuery,
  useGetProjectStatsQuery,
} = adminProjectsApi;
